import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { servicesAPI } from '../utils/api';
import './Home.css';

const categories = [
  { name: 'Plumber', icon: '🔧', desc: 'Pipe & water repairs' },
  { name: 'Electrician', icon: '⚡', desc: 'Wiring & electrical' },
  { name: 'Cleaner', icon: '🧹', desc: 'Home & office cleaning' },
  { name: 'Tutor', icon: '📚', desc: 'All subjects & levels' },
  { name: 'Beautician', icon: '💄', desc: 'Beauty & grooming' },
  { name: 'Carpenter', icon: '🪵', desc: 'Furniture & woodwork' },
  { name: 'Painter', icon: '🎨', desc: 'Interior & exterior' },
  { name: 'AC Repair', icon: '❄️', desc: 'AC service & repair' },
];

const stats = [
  { value: '500+', label: 'Verified Providers' },
  { value: '10,000+', label: 'Happy Customers' },
  { value: '8', label: 'Service Categories' },
  { value: '4.8★', label: 'Average Rating' },
];

const Home = () => {
  const [search, setSearch] = useState('');
  const [featuredServices, setFeaturedServices] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    servicesAPI.getAll().then(res => setFeaturedServices(res.data.slice(0, 6))).catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/services?search=${search}`);
  };

  return (
    <div className="home">
      {/* Hero */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-badge">🏆 Pakistan's #1 Service Marketplace</div>
          <h1>Find Trusted Local <span>Service Providers</span></h1>
          <p>Connect with verified electricians, plumbers, cleaners, tutors and more. Book instantly, pay securely, rate your experience.</p>
          <form className="hero-search" onSubmit={handleSearch}>
            <input
              type="text"
              placeholder="What service do you need? (e.g. Plumber, Tutor)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button type="submit">Search</button>
          </form>
          <div className="hero-tags">
            {['Plumber', 'Electrician', 'Cleaner', 'Tutor'].map(tag => (
              <button key={tag} onClick={() => navigate(`/services?category=${tag}`)} className="tag-pill">{tag}</button>
            ))}
          </div>
        </div>
        <div className="hero-image">
          <div className="hero-card">
            <div className="hero-card-avatar">AK</div>
            <div>
              <strong>Ahmed Khan</strong>
              <div style={{fontSize:'12px',color:'#64748b'}}>Certified Electrician</div>
              <div style={{color:'#F59E0B',fontSize:'13px'}}>★★★★★ 4.9 (238 reviews)</div>
            </div>
          </div>
          <div className="hero-card" style={{marginLeft:'2rem',marginTop:'1rem'}}>
            <div className="hero-card-avatar" style={{background:'#16A34A'}}>SR</div>
            <div>
              <strong>Sara Raza</strong>
              <div style={{fontSize:'12px',color:'#64748b'}}>Professional Cleaner</div>
              <div style={{color:'#F59E0B',fontSize:'13px'}}>★★★★★ 4.8 (185 reviews)</div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, i) => (
              <div key={i} className="stat-item">
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="categories-section">
        <div className="container">
          <div className="section-header">
            <h2>Browse by Category</h2>
            <p>Find the right professional for every home need</p>
          </div>
          <div className="categories-grid">
            {categories.map((cat) => (
              <Link key={cat.name} to={`/services?category=${cat.name}`} className="category-card">
                <div className="category-icon">{cat.icon}</div>
                <div className="category-name">{cat.name}</div>
                <div className="category-desc">{cat.desc}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Services */}
      {featuredServices.length > 0 && (
        <section className="featured-section">
          <div className="container">
            <div className="section-header">
              <h2>Featured Services</h2>
              <Link to="/services" className="view-all">View All →</Link>
            </div>
            <div className="services-grid">
              {featuredServices.map(service => (
                <Link key={service._id} to={`/services/${service._id}`} className="service-card">
                  <div className="service-card-header">
                    <div className="service-avatar">{service.provider?.name?.charAt(0) || 'P'}</div>
                    <div>
                      <div className="service-provider-name">{service.provider?.name}</div>
                      <div className="service-location">📍 {service.city}</div>
                    </div>
                    <div className="service-badge">{service.category}</div>
                  </div>
                  <div className="service-title">{service.title}</div>
                  <div className="service-desc">{service.description?.substring(0, 80)}...</div>
                  <div className="service-footer">
                    <div className="service-price">Rs. {service.price} <span>/{service.priceType?.replace('_', ' ')}</span></div>
                    <div className="service-rating">★ {service.rating?.toFixed(1) || 'New'}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* How it works */}
      <section className="how-section">
        <div className="container">
          <div className="section-header">
            <h2>How It Works</h2>
            <p>Book a service in 3 easy steps</p>
          </div>
          <div className="steps-grid">
            {[
              { step: '01', title: 'Search & Browse', desc: 'Search for the service you need and browse verified providers in your city.' },
              { step: '02', title: 'Book & Confirm', desc: 'Choose your preferred provider, select a time slot, and confirm your booking.' },
              { step: '03', title: 'Get Service & Review', desc: 'Provider comes to you, completes the job. Pay and leave an honest review.' },
            ].map((s) => (
              <div key={s.step} className="step-card">
                <div className="step-number">{s.step}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-box">
            <div className="cta-text">
              <h2>Are You a Service Provider?</h2>
              <p>Join thousands of professionals earning more with SmartService. Create your profile, set your rates, and start getting bookings today.</p>
            </div>
            <Link to="/register" className="cta-btn">Join as Provider →</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
